// SPEC-213 (Gestión del Círculo de Miembros) será la dueña de este módulo:
// llega a la misma ficha del referido por una ruta más corta, centrada en el
// miembro. SPEC-209 §4/§5 lo abre desde el detalle del avance del Gran
// Premio. El formulario de registrar/anular acciones de referido tiene **un
// solo lugar** por ANNEX-015 §2.3 — cualquier cambio a sus campos,
// validaciones o mensajes se hace acá, no en una segunda copia.
//
// Regla de dependencia de esta corrida (2026-09-07): la arista entre
// features del portal es unidireccional, `grand-prize → referrals`. Este
// módulo nunca importa de `grand-prize` ni de `members` — cualquier dato de
// esos módulos que necesite este panel entra por props.
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Can } from "@core/permissions";
import { ApiError } from "@core/http";
import { useSession } from "@core/session/SessionProvider";
import { tokens } from "@core/theme/tokens";
import {
  Badge,
  Field,
  FeedbackState,
  inputStyle,
  PortalButton,
  RowIconAction,
  SidePanel,
  useToast,
} from "@portal/common";
import { markErrorHandled, reportError } from "@portal/common/errorMessage";
import { catalogLabel } from "../points/api";
import { useAdjustmentReasons } from "../points/pointQueries";
import { formatShortDate } from "../vehicles/dateFormat";
import {
  REFERRAL_ERROR_MESSAGES,
  REFERRAL_OLD_DATE_NOTICE,
  referralWarningText,
  type ReferralAction,
} from "./api";
import {
  useReferralActionCatalog,
  useReferralDetail,
  useReferralMutations,
} from "./referralQueries";

const registerSchema = z.object({
  actionId: z.string().min(1, "Selecciona la acción"),
  date: z.string().min(1, "Ingresa la fecha"),
  quantity: z.coerce.number().int().min(1).optional(),
  reference: z.string().optional(),
});
type RegisterValues = z.infer<typeof registerSchema>;

const voidSchema = z.object({
  adjustmentReasonId: z.string().min(1, "Selecciona el motivo del ajuste"),
  detail: z.string().optional(),
});
type VoidValues = z.infer<typeof voidSchema>;

const STATUS_LABEL: Record<ReferralAction["status"], string> = {
  current: "Vigente",
  voided: "Anulada",
};

// admin.jsx:1226 (fila anulada, atenuada, nunca tachada — G-006) y este
// mismo criterio en points/PointsPanel.tsx.
const VOIDED_OPACITY = 0.55;

// `first` separa filas con `borderTop` en todas menos la primera, el mismo
// criterio que `PlaceCheckboxList`. Antes era `borderBottom` en todas: la
// última fila dejaba una línea divisoria pegada al borde inferior de la
// tarjeta y la lista se veía apretada contra "Registrar acción" (revisión en
// vivo 2026-09-22, punto #14). Con `borderTop` no sobra ninguna línea al
// final y el patrón de separador de listas queda unificado en el portal.
function ActionRow({
  action,
  onVoid,
  canVoid,
  first,
}: {
  action: ReferralAction;
  onVoid: (action: ReferralAction) => void;
  canVoid: boolean;
  first: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        padding: "10px 16px",
        borderTop: first ? "none" : `1px solid ${tokens.surface.line}`,
        opacity: action.status === "voided" ? VOIDED_OPACITY : 1,
      }}
    >
      <div>
        <div style={{ font: `600 12.5px ${tokens.font.ui}` }}>
          {action.action.name ?? "—"}
          {action.action.isFinal && action.quantity > 1
            ? ` × ${action.quantity}`
            : ""}
        </div>
        <div
          style={{
            font: `400 11.5px/1.6 ${tokens.font.ui}`,
            color: tokens.text.tertiary,
          }}
        >
          {[
            formatShortDate(action.occurredOn),
            action.registeredBy,
            action.reference,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Badge tone={action.status === "current" ? "silver" : "neutral"}>
          {STATUS_LABEL[action.status]}
        </Badge>
        {/* Revisión en vivo 2026-09-22, punto #10 (barrido): misma situación
          que "Anular" en Movimientos de puntos — acción de fila riesgosa
          (deshace una acción ya registrada del referido) dibujada con
          `.btn-link`, pegada al `Badge` de estado y sin ninguna señal de ser
          consecuente. Mismo ícono, mismo tono de peligro. */}
        {canVoid && action.status === "current" ? (
          <RowIconAction
            label="Anular"
            icon="revert"
            tone="danger"
            onClick={() => onVoid(action)}
          />
        ) : null}
      </div>
    </div>
  );
}

export function ReferralActionsPanel({
  referralId,
  progressId,
  memberId,
  onClose,
}: {
  referralId: string;
  progressId?: string;
  memberId?: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const { hasPermission } = useSession();
  // ANNEX-015 §2.4 y api/src/infrastructure/http/routes/referral-actions.routes.ts:24-74:
  // ambas operaciones se gatean sobre el módulo `members`, no `prizes` —
  // registrar exige `canCreate`, anular exige `canEdit`.
  const canVoid = hasPermission("members", "canEdit");
  const detail = useReferralDetail(referralId);
  const mutations = useReferralMutations(referralId, progressId, memberId);
  const catalog = useReferralActionCatalog();
  const reasons = useAdjustmentReasons();
  const [needsDateConfirmation, setNeedsDateConfirmation] = useState(false);
  const [registerKey, setRegisterKey] = useState<string | null>(null);
  const [voiding, setVoiding] = useState<ReferralAction | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    reset,
    formState: { errors },
  } = useForm<RegisterValues>({
    defaultValues: { actionId: "", date: "", reference: "" },
  });
  const actionId = watch("actionId");
  const entries = catalog.data ?? [];
  // Determinar la acción que cierra el Gran Premio: si ya hay una fila
  // registrada con `action.isFinal`, se usa ese id; si no, se asume la de
  // mayor `sortOrder` del catálogo (ANEXO-014). El servidor es siempre
  // autoritativo: si esta suposición fuera incorrecta, ignora `quantity` en
  // vez de aceptarla, así que el riesgo es solo de UX, no de negocio.
  const finalFromHistory = (detail.data?.actions ?? []).find(
    (a) => a.action.isFinal,
  )?.action.id;
  const finalById =
    finalFromHistory ??
    entries.reduce<string | undefined>((best, entry) => {
      if (!best) return entry.id;
      const current = entries.find((e) => e.id === best);
      return (entry.sortOrder ?? 0) > (current?.sortOrder ?? 0)
        ? entry.id
        : best;
    }, undefined);
  const isFinalSelected = Boolean(actionId) && actionId === finalById;

  const {
    register: registerVoid,
    handleSubmit: handleVoidSubmit,
    reset: resetVoid,
    setError: setVoidError,
    formState: { errors: voidErrors },
  } = useForm<VoidValues>({
    defaultValues: { adjustmentReasonId: "", detail: "" },
  });

  const submitRegister = (value: RegisterValues) => {
    const parsed = registerSchema.safeParse(value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setError(issue.path[0] as keyof RegisterValues, {
          message: issue.message,
        });
      }
      return;
    }
    const idempotencyKey = registerKey ?? crypto.randomUUID();
    setRegisterKey(idempotencyKey);
    // SPEC-012 criterio 12: la reconfirmación de `OLD_DATE` se decide con un
    // booleano explícito, nunca leyendo el texto del aviso.
    const confirmed = needsDateConfirmation;
    mutations.register.mutate(
      {
        input: {
          actionId: parsed.data.actionId,
          date: parsed.data.date,
          ...(isFinalSelected && parsed.data.quantity
            ? { quantity: parsed.data.quantity }
            : {}),
          ...(parsed.data.reference?.trim()
            ? { reference: parsed.data.reference.trim() }
            : {}),
          confirmed,
        },
        idempotencyKey,
      },
      {
        onSuccess: () => {
          toast.show("Acción registrada.");
          setNeedsDateConfirmation(false);
          setRegisterKey(null);
          reset({ actionId: "", date: "", reference: "" });
        },
        onError: (error) => {
          if (error instanceof ApiError && error.code === "OLD_DATE") {
            setNeedsDateConfirmation(true);
            markErrorHandled(error);
            return;
          }
          reportError(toast, error, REFERRAL_ERROR_MESSAGES);
        },
      },
    );
  };

  const submitVoid = (value: VoidValues) => {
    if (!voiding) return;
    const parsed = voidSchema.safeParse(value);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        setVoidError(issue.path[0] as keyof VoidValues, {
          message: issue.message,
        });
      }
      return;
    }
    mutations.void.mutate(
      {
        actionId: voiding.id,
        input: {
          adjustmentReasonId: parsed.data.adjustmentReasonId,
          ...(parsed.data.detail?.trim()
            ? { detail: parsed.data.detail.trim() }
            : {}),
        },
      },
      {
        onSuccess: (result) => {
          const warning = result.warnings
            ?.map((w) => referralWarningText(w.code))
            .find((text): text is string => Boolean(text));
          toast.show(
            warning ?? "Acción anulada.",
            warning ? "neutral" : "success",
          );
          setVoiding(null);
          resetVoid();
        },
        onError: (error) => reportError(toast, error, REFERRAL_ERROR_MESSAGES),
      },
    );
  };

  return (
    <SidePanel label="Ficha del referido" width={520} onClose={onClose}>
      <div
        style={{
          padding: "22px 26px",
          borderBottom: `1px solid ${tokens.surface.line}`,
        }}
      >
        <div
          className="label"
          style={{ marginBottom: 6, color: tokens.text.tertiary }}
        >
          Ficha del referido
        </div>
        <h2
          className="display"
          style={{ fontSize: 23, margin: 0, lineHeight: 1.06 }}
        >
          {detail.data?.name ?? "—"}
        </h2>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "22px 26px 30px" }}>
        {detail.isLoading ? <FeedbackState kind="loading" /> : null}
        {detail.isError ? <FeedbackState kind="error" /> : null}
        {detail.data ? (
          <>
            <div
              style={{
                border: `1px solid ${tokens.surface.line}`,
                borderRadius: tokens.radius.sm,
                overflow: "hidden",
                marginBottom: 16,
              }}
            >
              <div style={{ padding: "14px 16px 10px" }}>
                <span className="label">Acciones registradas</span>
              </div>
              {detail.data.actions.length === 0 ? (
                <div
                  style={{
                    padding: "0 16px 14px",
                    color: tokens.text.tertiary,
                    font: `400 12px ${tokens.font.ui}`,
                  }}
                >
                  Este referido todavía no tiene acciones registradas.
                </div>
              ) : (
                detail.data.actions.map((action, index) => (
                  <ActionRow
                    key={action.id}
                    action={action}
                    canVoid={canVoid}
                    onVoid={setVoiding}
                    first={index === 0}
                  />
                ))
              )}
            </div>

            <Can module="members" action="canCreate">
              <form onSubmit={handleSubmit(submitRegister)} noValidate>
                <div className="label" style={{ marginBottom: 10 }}>
                  Registrar acción
                </div>
                <Field label="Acción" required error={errors.actionId?.message}>
                  <select
                    aria-label="Acción"
                    style={inputStyle}
                    {...register("actionId")}
                  >
                    <option value="">Selecciona una acción</option>
                    {entries.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {catalogLabel(entry)}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Fecha" required error={errors.date?.message}>
                  <input
                    aria-label="Fecha"
                    type="date"
                    style={inputStyle}
                    {...register("date")}
                  />
                </Field>
                {isFinalSelected ? (
                  <Field label="Cantidad" error={errors.quantity?.message}>
                    <input
                      aria-label="Cantidad"
                      type="number"
                      min={1}
                      style={inputStyle}
                      {...register("quantity")}
                    />
                  </Field>
                ) : null}
                <Field label="Referencia">
                  <input
                    aria-label="Referencia"
                    style={inputStyle}
                    {...register("reference")}
                  />
                </Field>
                {needsDateConfirmation ? (
                  <div
                    role="alert"
                    style={{
                      marginBottom: 12,
                      padding: "10px 13px",
                      borderRadius: 11,
                      border: `1px solid ${tokens.danger.line}`,
                      background: tokens.danger.dim,
                      font: `400 12px ${tokens.font.ui}`,
                      color: tokens.danger.base,
                    }}
                  >
                    {REFERRAL_OLD_DATE_NOTICE}
                  </div>
                ) : null}
                <PortalButton
                  tone="primary"
                  size="small"
                  disabled={mutations.register.isPending}
                >
                  {mutations.register.isPending
                    ? "Registrando…"
                    : needsDateConfirmation
                      ? "Confirmar de todas formas"
                      : "Registrar"}
                </PortalButton>
              </form>
            </Can>
          </>
        ) : null}
      </div>

      {voiding ? (
        <SidePanel
          label="Anular acción"
          width={420}
          onClose={() => setVoiding(null)}
        >
          <form
            onSubmit={handleVoidSubmit(submitVoid)}
            noValidate
            style={{ padding: 22 }}
          >
            <h3
              className="display"
              style={{ fontSize: 20, margin: "0 0 16px", lineHeight: 1.06 }}
            >
              Anular acción
            </h3>
            <Field
              label="Motivo del ajuste"
              required
              error={voidErrors.adjustmentReasonId?.message}
            >
              <select
                aria-label="Motivo del ajuste"
                style={inputStyle}
                {...registerVoid("adjustmentReasonId")}
              >
                <option value="">Selecciona un motivo…</option>
                {(reasons.data ?? []).map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {catalogLabel(entry)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Detalle adicional">
              <textarea
                aria-label="Detalle adicional"
                style={{
                  ...inputStyle,
                  minHeight: 66,
                  resize: "vertical" as const,
                }}
                {...registerVoid("detail")}
              />
            </Field>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}
            >
              <button
                type="button"
                className="btn-link"
                onClick={() => setVoiding(null)}
              >
                Cancelar
              </button>
              <PortalButton
                tone="primary"
                size="small"
                disabled={mutations.void.isPending}
              >
                {mutations.void.isPending ? "Anulando…" : "Anular"}
              </PortalButton>
            </div>
          </form>
        </SidePanel>
      ) : null}
    </SidePanel>
  );
}
